import React from 'react';
import { useAuth } from 'react-oidc-context';
import IFlightRangeData from '../../types/FlightRanges';
import NotificationService from '../../services/NotificationService';

interface FlightRangeHistoryRowProps {
  range: IFlightRangeData;
  'aria-rowindex'?: number;
}

/**
 * Individual history row component following SRP
 * Clear naming conventions and accessibility compliance
 */
const FlightRangeHistoryRow: React.FC<FlightRangeHistoryRowProps> = ({ 
  range, 
  'aria-rowindex': ariaRowIndex 
}) => {
  const auth = useAuth();

  /**
   * Formats flight range as "start - end"
   * Low complexity utility function
   */
  const formatFlightRange = (start: number, end: number): string => 
    `${start} - ${end}`;

  /**
   * Returns discontinued date in YYYY-MM-DD format or empty string for active ranges
   */
  const getDiscontinuedStatus = (): string => {
    if (!range.discontinueDate) return '';
    return range.discontinueDate; // Already in YYYY-MM-DD format
  };

  /**
   * Handles PDF view when user clicks the eye icon
   */
  const handleViewPdf = async () => {
    if (!range.pdfBlobName) {
      alert('No notification PDF available for this modification.');
      return;
    }

    try {
      const token = auth.user?.access_token;
      const blob = await NotificationService.downloadNotificationPdf(range.pdfBlobName, token!);

      if (blob.size === 0) {
        alert('PDF file is empty. Please contact support.');
        return;
      }

      // Force the blob type to be application/pdf if it isn't already
      const pdfBlob = blob.type === 'application/pdf'
        ? blob
        : new Blob([blob], { type: 'application/pdf' });

      // Create a blob URL
      const url = URL.createObjectURL(pdfBlob);

      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = url;

      // Set download attribute to provide the correct filename when user saves/downloads
      link.download = range.pdfBlobName;

      // Also open in new tab for viewing (browser will use built-in PDF viewer)
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Append to body temporarily (required for Firefox)
      document.body.appendChild(link);

      // Trigger click
      link.click();

      // Clean up
      document.body.removeChild(link);

      // Revoke the blob URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error('Error viewing PDF:', err);
      alert('Failed to load PDF. Please try again.');
    }
  };

  return (
    <tr 
      role="row"
      className="history-row"
      aria-rowindex={ariaRowIndex}
    >
      <td>
        <span className="text-font">
          {range.id}
        </span>
      </td>
      <td>
        <span className="text-font">
          {range.marketingCarrier}
        </span>
      </td>
      <td>
        <span className="text-font">
          {formatFlightRange(range.start, range.end)}
        </span>
      </td>
      <td>
        <span className="text-font">
          {range.operatingCarrier}
        </span>
      </td>
      <td>
        <span className="text-font">
          {range.flightTypeDescription}
        </span>
      </td>
      <td>
        <span className="text-success">
          {range.effectiveDate || 'N/A'}
        </span>
      </td>
      <td>
        <span className="text-danger">
          {getDiscontinuedStatus()}
        </span>
      </td>
      <td>
        <div className="modified-info">
          <div className="modified-content">
            <time dateTime={range.modDate} title={`Modified: ${range.modDate}`}>
              {range.modDate}
            </time>
            <div className="modified-by">
              {range.modId}
            </div>
          </div>
          <button
            type="button"
            onClick={handleViewPdf}
            className="modified-icon"
            disabled={!range.pdfBlobName}
            title={range.pdfBlobName ? `View notification PDF: ${range.pdfBlobName}` : 'No notification PDF available'}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: range.pdfBlobName ? 'pointer' : 'default',
              opacity: range.pdfBlobName ? 1 : 0.5
            }}
          >
            <img
              src="/view-history.svg"
              alt={range.pdfBlobName ? 'View notification PDF' : 'No notification PDF'}
              width="24"
              height="18"
            />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default FlightRangeHistoryRow;